import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Container } from "react-bootstrap";
import { ArrowLeft, ArrowRight, Calendar, Clock, Link2, Share2 } from "lucide-react";
import { FaWhatsapp, FaFacebookF, FaTwitter } from "react-icons/fa";
import { formatDate } from "../../helper/Helper";
import Breadcrump from "../../components/Breadcrump";
import blogAPI from "../../api/BlogApi";
import SEO from "../../components/SEO";
import BlogRelatedCard from "./relatedCard";
import { showError, showSuccess } from "../../utils/toastUtils";
import "./blogDetail.css";

function getInitials(name = "") {
  const words = name.replace(/^dr\.?\s+/i, "").trim().split(" ").filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function DetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchBlogsDetail = async () => {
    try {
      setLoading(true);
      const res = await blogAPI.details(slug, { limit: 4 });
      if (res.code === 200) {
        const { blog, relatedBlogs } = res.data;
        setItem(blog);
        setRelatedBlogs(relatedBlogs || []);
        setMetaData(blog?.meta || {});
      }
    } catch (err) {
      console.error("Error loading blogs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogsDetail();
    window.scrollTo({ top: 0 });
  }, [slug]);

  /******************* SHARE *******************/
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${item?.title || "Blog"} 👇`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: item?.title, text: shareText, url: shareUrl });
      } catch (e) {
        /* user ne cancel kiya */
      }
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccess("Link copied!");
    } catch {
      showError("Could not copy link");
    }
  };

  const category =
    typeof item?.category === "object" ? item?.category?.name : item?.category;

  /******************* LOADING *******************/
  if (loading && !item) {
    return (
      <>
        <Breadcrump title="Blog" middle="" />
        <div className="bd-page">
          <Container>
            <div className="bd-grid">
              <div className="bd-main">
                <div className="bd-skel bd-skel-line" style={{ width: "30%" }} />
                <div className="bd-skel bd-skel-title" />
                <div className="bd-skel bd-skel-title" style={{ width: "70%" }} />
                <div className="bd-skel bd-skel-cover" />
                <div className="bd-skel bd-skel-line" />
                <div className="bd-skel bd-skel-line" />
                <div className="bd-skel bd-skel-line" style={{ width: "80%" }} />
              </div>
            </div>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      <Breadcrump title={item?.title} middle="" />
      <SEO meta={metaData} />

      <div className="bd-page">
        <Container>
          <Link to="/blog" className="bd-back">
            <ArrowLeft size={15} /> Back to Blogs
          </Link>

          <div className="bd-grid">
            {/* ───────── MAIN ───────── */}
            <main className="bd-main">
              <header className="bd-header">
                <span className="bd-kicker">
                  <span className="bd-kicker-dot" />
                  {category || "Journal / Story"}
                </span>

                <h1 className="bd-title">{item?.title}</h1>

                {item?.shortDescription && (
                  <p className="bd-subtitle">{item.shortDescription}</p>
                )}

                <div className="bd-meta">
                  <div className="bd-author">
                    <div className="bd-avatar">{getInitials(item?.author || "Team")}</div>
                    <div>
                      <div className="bd-author-name">{item?.author || "Team"}</div>
                      <div className="bd-author-role">Author</div>
                    </div>
                  </div>

                  <div className="bd-meta-chips">
                    {item?.date && (
                      <span className="bd-chip">
                        <Calendar size={14} />
                        {formatDate(item.date)}
                      </span>
                    )}
                    {item?.readTime && (
                      <span className="bd-chip">
                        <Clock size={14} />
                        {item.readTime}
                      </span>
                    )}
                  </div>
                </div>
              </header>

              {/* Landscape cover — poori image dikhti hai (no crop) */}
              {item?.image && (
                <figure className="bd-cover">
                  <div
                    className="bd-cover-bg"
                    style={{ backgroundImage: `url("${item.image}")` }}
                    aria-hidden="true"
                  />
                  <img src={item.image} alt={item?.title || ""} className="bd-cover-img" />
                </figure>
              )}

              <article
                className="bd-content"
                dangerouslySetInnerHTML={{ __html: item?.description }}
              />

              {/* Share */}
              <div className="bd-share">
                <span className="bd-share-label">
                  <Share2 size={15} /> Share this story
                </span>

                <div className="bd-share-btns">
                  <a
                    className="bd-share-btn"
                    href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on WhatsApp"
                  >
                    <FaWhatsapp size={16} />
                  </a>
                  <a
                    className="bd-share-btn"
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on Facebook"
                  >
                    <FaFacebookF size={15} />
                  </a>
                  <a
                    className="bd-share-btn"
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on X"
                  >
                    <FaTwitter size={15} />
                  </a>
                  <button
                    type="button"
                    className="bd-share-btn"
                    onClick={copyLink}
                    aria-label="Copy link"
                  >
                    <Link2 size={16} />
                  </button>
                  <button
                    type="button"
                    className="bd-share-btn bd-share-more"
                    onClick={handleNativeShare}
                  >
                    <Share2 size={15} /> Share
                  </button>
                </div>
              </div>
            </main>

            {/* ───────── SIDEBAR ───────── */}
            <aside className="bd-sidebar">
              <div className="bd-sidebar-inner">
                <div className="bd-sidebar-head">
                  <h2 className="bd-sidebar-title">Related Blogs</h2>
                  <Link to="/blog" className="bd-viewall">
                    View all <ArrowRight size={13} />
                  </Link>
                </div>

                {relatedBlogs.length > 0 ? (
                  <div className="bd-related-list">
                    {relatedBlogs.map((blog, index) => (
                      <BlogRelatedCard key={blog._id || index} item={blog} />
                    ))}
                  </div>
                ) : (
                  <p className="bd-empty">No related blogs yet.</p>
                )}
              </div>
            </aside>
          </div>
        </Container>
      </div>
    </>
  );
}

export default DetailPage;
